import React, { Fragment } from 'react';
import qs from 'qs';
import { Link, Route, matchPath } from 'react-router-dom';
import { Transition } from 'react-transition-group';
import { Helmet } from 'react-helmet';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import memoize from 'lodash.memoize';
import BasicLoader from 'components/BasicLoader';
import FontAwesomeIcon from 'components/FontAwesomeIcon';
import ImageWorks from 'components/WorkPage/ImageWorks';
import VideoWorks from 'components/WorkPage/VideoWorks';
import WebWorks from 'components/WorkPage/WebWorks';
import SoundWorks from 'components/WorkPage/SoundWorks';
import ThreeDModelWorks from 'components/WorkPage/ThreeDModelWorks';
import WorkDetailDesktop from 'components/WorkPage/WorkDetailDesktop';
import WorkCredits from 'components/WorkPage/WorkCredits';
import Header from 'components/Header';
import WorkpageNavigationBar from 'components/WorkPage/WorkPageNavigationBar';
import ShareWorkOrNote from 'components/WorkPage/ShareWorkOrNote';
import RelatedWorks from 'components/WorkPage/RelatedWorks';
import NoteBody from 'components/WorkPage/NoteBody';
import AboutModal from 'components/AboutModal';
import FloatingButton from 'components/FloatingButton';
import errorHandler from 'components/ErrorPage/errorHandler';
import { launchNotification } from 'components/Notifications/notifActions';
import Divider from 'components/Divider';
import { GrayButton } from 'components/Buttons';
import {
  getSingleWork,
  getSingleWorkNotesList,
  getUserProfile,
} from 'services/api';
import { media, mediaMin, withResponsive, deviceSizes } from 'util/Responsive';
import { getBaseUrl, getOGUrl } from 'util/GetBaseUrl';
import { translate } from 'util/Translator';
import { buildCanonicalMediaUrl } from 'util/mediaUrl';
import {
  buildNotePath,
  buildNotesPath,
  buildWorkPath,
  findWorkStateBySignedRef,
} from 'util/workRoute';

const HeaderWrap = styled.div`
  width: 100%;
  position: relative;
  margin-top: 80px;
  padding-left: 30px;
  padding-right: 30px;
  background-color: white;
  ${media.tablet`
    margin-top: 30px;
    width: auto;
  `}
`;

const WorkAndDesktopNoteWrapContainer = styled.div`
  width: 100vw;
  display: flex;
  justify-content: center;
  background-color: white;
`;

const WorkAndDesktopNoteWrap = styled.div`
  display: flex;
  max-width: 1500px;
  margin: 0 auto;
  flex-direction: row;
  align-items: stretch;
  width: 100% !important;

  ${mediaMin.tablet`
    height: calc(100vh - 100px);
  `}
`;

const WorkBody = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-self: baseline;
  transition: all 0.7s;
  flex: 1;
  width: 100%;
  background-color: white;
`;

const WorkContainer = styled.div`
  padding: 80px 20px 0px;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  background-color: white;

  ${media.phone`
    padding: 20px;
  `}

  ${mediaMin.tablet`
    overflow-y: scroll;
    height: calc(100vh - 100px);
    ${({ isVerticalCenter }) =>
      isVerticalCenter
        ? css`
            display: flex;
            justify-content: center;
            align-items: center;
            display: flex;
            flex-direction: column;
            padding: 20px;
          `
        : css`
            padding: 80px 20px 0;
          `};
  `}
`;

const Title = styled.h1`
  font-size: 31px;
  font-weight: bold;
  line-height: 31px;
  text-align: center;
  color: #333333;
  word-break: break-word;
  margin: 0;
  ${media.tablet`
    font-size: 25px;
  `}
`;

const AboutButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 23px;
`;

const BottomDivider = styled(Divider)`
  background-color: #f2f2f2;
  margin-top: 50px;
  margin-bottom: 25px;
  ${media.tablet`
    margin-top: 10px;
    margin-bottom: 20px;
  `}
`;

const WorkNotePageWrap = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;
const AllWorkInfoWrap = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex-shrink: 0;
  transition: all 0.7s;
  width: 100%;
  background-color: white;
`;

const PrivateWorkFloatingButton = styled(FloatingButton)`
  && {
    background-color: #828282;
    border-radius: 15px;
    bottom: 30px;
    right: initial;
    left: 30px;
    text-align: left;
    :hover {
      background-color: rgb(123, 123, 123);
    }
    :active {
      background-color: rgb(108, 116, 116);
    }
    ${media.tablet`
      left: 15px;
      right: 15px;
      border-radius: 5px;
    `}
  }
`;

const PrivateWorkFloatingButtonContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 20px;
  align-items: center;
  ${media.tablet`
    padding: 9px 20px;
  `}
`;

const PrivateWorkFloatingButtonIconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-right: 15px;
`;

const PrivateWorkFloatingButtonTitle = styled.div`
  font-weight: bold;
  font-size: 21px;
  line-height: 21px;
  letter-spacing: 0.03em;
  color: #fbfbfb;
  ${media.tablet`
    font-size: 18px;
    line-height: 18px;
  `}
`;

const PrivateWorkFloatingButtonDescription = styled.div`
  margin-top: 8px;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.03em;
  color: #fbfbfb;
`;

export class WorkPage extends React.Component {
  static getInitialData(ctx) {
    const { match, dispatch, queryString, screenName } = ctx;
    const { owner_screen_name, signed_ref } = match.params;
    const { secret } = queryString;
    return Promise.all([
      getSingleWork(
        dispatch,
        owner_screen_name,
        signed_ref,
        undefined,
        secret,
      ),
      getUserProfile(dispatch, screenName),
    ]).then(async ([work]) => {
      if (work) {
        await getSingleWorkNotesList(
          dispatch,
          work.author?.screen_name || owner_screen_name,
          signed_ref,
          work.id,
          secret,
        );
      }
      return {
        match,
      };
    });
  }

  constructor(props) {
    super(props);
    const { location } = props;
    const isMatchNoteRoutes = !!matchPath(location.pathname, {
      path: '/works/:owner_screen_name/:signed_ref/notes',
    });
    this.state = {
      hideNoteBody: !isMatchNoteRoutes,
      isOpenAboutModal: false,
      isShowNote: isMatchNoteRoutes,
      isOpenLightbox: false,
      selectedLightboxIndex: null,
      lightboxViewIndex: null,
    };
    this.memoizedMapImages = memoize(this.mapImages);
  }

  componentDidMount() {
    const { match, initialData, dispatch, location, screenName } = this.props;
    // Skip fetching if data fetched from server side
    if (
      !initialData ||
      match.params.signed_ref !== initialData.match.params.signed_ref
    ) {
      const queryString = qs.parse(location.search, {
        ignoreQueryPrefix: true,
      });
      this.constructor.getInitialData({
        match,
        dispatch,
        screenName,
        queryString,
      });
    }
  }

  componentWillUpdate(nextProps) {
    const { location } = this.props;
    if (!this.previousLocation && nextProps.history.action !== 'POP') {
      this.previousLocation = location;
    }
  }

  componentDidUpdate(prevProps) {
    const {
      signed_ref: oldSignedRef,
      location: prevLocation,
      dispatch,
    } = prevProps;
    const { signed_ref, location, match, screenName } = this.props;
    const prevIsMatchNoteRoutes = !!matchPath(prevLocation.pathname, {
      path: '/works/:owner_screen_name/:signed_ref/notes',
    });
    const isMatchNoteRoutes = !!matchPath(location.pathname, {
      path: '/works/:owner_screen_name/:signed_ref/notes',
    });
    if (isMatchNoteRoutes !== prevIsMatchNoteRoutes) {
      if (isMatchNoteRoutes) {
        this.setState({
          isShowNote: true,
          hideNoteBody: false,
        });
      } else {
        this.setState({
          isShowNote: false,
        });
      }
    }

    if (signed_ref && signed_ref !== oldSignedRef) {
      const queryString = qs.parse(location.search, {
        ignoreQueryPrefix: true,
      });
      this.workPageTitlePosition = null;
      this.constructor.getInitialData({
        match,
        dispatch,
        queryString,
        screenName,
      });
      if (isMatchNoteRoutes) {
        this.setState({
          isShowNote: true,
          hideNoteBody: false,
        });
      } else {
        this.setState({
          isShowNote: false,
        });
      }
    }
  }

  handleOnPressOpenAboutModal = () => {
    this.setState({
      isOpenAboutModal: true,
    });
  };

  handleOnPressCloseAboutModal = () => {
    this.setState({
      isOpenAboutModal: false,
    });
  };

  handleOnClickCopyPrivateWorkUrl = () => {
    const { dispatch } = this.props;
    launchNotification(dispatch, { message: 'urlCopied', type: 'success' });
  };

  handleOnNoteTransitionEnd = () => {
    const { isShowNote, hideNoteBody } = this.state;
    if (!isShowNote && !hideNoteBody) {
      this.setState({
        hideNoteBody: true,
      });
    }
  };

  handleOnClickViewNotes = () => {
    this.scrollToWorkBody();
  };

  handleOnClickViewNote = () => {
    this.scrollToWorkBody();
  };

  handleOnToggleLightbox = index => {
    const { isOpenLightbox } = this.state;
    this.setState({
      isOpenLightbox: !isOpenLightbox,
      selectedLightboxIndex: index,
      lightboxViewIndex: index,
    });
  };

  handleOnLightboxViewIndeChange = index => {
    this.setState({
      lightboxViewIndex: index,
    });
  };

  scrollToWorkBody = () => {
    const { appWidth } = this.props;
    if (this.workBody && appWidth >= deviceSizes.tablet) {
      window.scrollTo({
        top: this.workBody.offsetTop,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  mapImages = images => {
    return images.map(image => {
      let imageFileName;
      const detailUrl = image?.urls?.detail2x;
      if (typeof detailUrl === 'string') {
        const candidate = detailUrl.split(/[?#]/)[0].split('/').pop();
        if (candidate && candidate.toLowerCase().endsWith('.gif')) {
          imageFileName = candidate;
        }
      }
      const gifUrl = imageFileName
        ? buildCanonicalMediaUrl('store', imageFileName)
        : null;
      return {
        ...image,
        id: image.id,
        url: gifUrl || detailUrl,
        srcset: gifUrl ? null : image.srcset,
      };
    });
  };

  renderWorks = () => {
    const {
      work: { data },
    } = this.props;
    const {
      isOpenLightbox,
      selectedLightboxIndex,
      lightboxViewIndex,
    } = this.state;
    if (data.type === 'image') {
      const images = this.memoizedMapImages(data.images);
      return <ImageWorks images={images} workTitle={data.title} />;
      // return (
      //   <ImageWorks
      //     images={data.images}
      //     workTitle={data.title}
      //     isOpenLightbox={isOpenLightbox}
      //     onToggleLightbox={this.handleOnToggleLightbox}
      //     onLightboxViewIndexChange={this.handleOnLightboxViewIndeChange}
      //     selectedLightboxIndex={selectedLightboxIndex}
      //     lightboxViewIndex={lightboxViewIndex}
      //   />
      // );
    }
    if (data.type === 'video') {
      return <VideoWorks videos={data.videos} workTitle={data.title} />;
    }
    if (data.type === 'web_article') {
      return <WebWorks articles={data.web_articles} workTitle={data.title} />;
    }
    if (data.type === 'sound') {
      return <SoundWorks sounds={data.sounds} workTitle={data.title} />;
    }
    if (data.type === 'embedded3d') {
      return (
        <ThreeDModelWorks models={data.embedded3ds} workTitle={data.title} />
      );
    }
    return null;
  };

  render() {
    const {
      t,
      user,
      work,
      works,
      noteMode,
      appWidth,
      staticContext,
      location,
    } = this.props;
    const { data, loading, notesList } = work;
    const { isOpenAboutModal, isShowNote } = this.state;
    const matchNotesRoute = matchPath(location.pathname, {
      path: '/works/:owner_screen_name/:signed_ref/notes',
      exact: true,
    });
    const isMobile = appWidth < deviceSizes.tablet;
    if (!data || !data.author || loading) {
      return <BasicLoader />;
    }
    const hasTiktokVideo =
      data.type === 'video' &&
      data.videos.some(video => video.platform === 'tiktok');

    const userWorks = user?.workIDs?.map(workId => works[workId]?.data);
    const currentWorkIndex = userWorks?.findIndex(w => +w.id === +work.data.id);

    const prevWork =
      currentWorkIndex !== -1 && currentWorkIndex > 0 && userWorks?.length > 0
        ? userWorks[currentWorkIndex - 1]
        : null;
    const nextWork =
      currentWorkIndex !== -1 && currentWorkIndex < userWorks?.length - 1
        ? userWorks[currentWorkIndex + 1]
        : null;

    return (
      <WorkNotePageWrap isMobile={isMobile}>
        {data && data.author && (
          <Helmet>
            <title>{`${data.title} - ${data.author.profile.name}`}</title>
            <meta name="description" content={`${data.description}`} />
            <meta
              property="og:title"
              content={`${data.title} - ${data.author.profile.name}`}
            />
            <meta property="og:url" content={getOGUrl(staticContext)} />
            <link rel="canonical" href={getOGUrl(staticContext)} />
            <meta property="og:description" content={`${data.description}`} />
            <meta property="og:image" content={`${data.thumbnail}`} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta
              name="twitter:title"
              content={`${data.title} - ${data.author.profile.name}`}
            />
            <meta
              name="twitter:text:title"
              content={`${data.title} - ${data.author.profile.name}`}
            />
            <meta name="twitter:description" content={`${data.description}`} />
            <meta name="twitter:image" content={`${data.thumbnail}`} />
            <link rel="shortcut icon" href={data.author.profile.avatar.thumb} />
            {(data.secret ||
              data.is_private ||
              data.author.hidden_from_search_engines) && (
              <meta name="robots" content="noindex, nofollow" />
            )}
          </Helmet>
        )}
        {!isMobile && <Header backText={t('back')} />}
        <AllWorkInfoWrap>
          {isMobile && (
            <HeaderWrap>
              <Title>{data.title}</Title>
              {data.description && data.description.length > 0 ? (
                <AboutButtonContainer>
                  <GrayButton
                    fullyRound
                    text={t('about')}
                    onPress={this.handleOnPressOpenAboutModal}
                  />
                </AboutButtonContainer>
              ) : null}
              <WorkCredits
                workOwnerId={data.owner_id}
                workId={data.id}
                credits={data.credits}
              />
            </HeaderWrap>
          )}
          <WorkAndDesktopNoteWrapContainer>
            <WorkAndDesktopNoteWrap>
              <WorkBody>
                <WorkContainer
                  isVerticalCenter={
                    (data.type === 'video' &&
                      data.videos &&
                      data.videos.length === 1) ||
                    (data.type === 'sound' &&
                      data.sounds &&
                      data.sounds.length === 1) ||
                    data.type === 'web_article'
                  }
                >
                  {this.renderWorks()}
                  {isMobile && (
                    <Fragment>
                      <BottomDivider />
                      {data && (
                        <ShareWorkOrNote
                          work={data}
                          staticContext={staticContext}
                        />
                      )}
                    </Fragment>
                  )}
                  {isMobile && notesList && !!notesList.length && !isShowNote && (
                    <Link
                      to={
                        notesList.length === 1
                          ? buildNotePath(
                              work.data,
                              notesList[0].id,
                              work.data.secret,
                            )
                          : buildNotesPath(
                              work.data,
                              work.data.secret,
                            )
                      }
                    >
                      <FloatingButton
                        icon={
                          <FontAwesomeIcon
                            icon={['fas', 'comment-dots']}
                            color="#F2F2F2"
                            size={28}
                          />
                        }
                        label={
                          notesList.length === 1
                            ? t('viewNote', { number: notesList.length })
                            : t('viewNotes', { number: notesList.length })
                        }
                        hideBadge
                      />
                    </Link>
                  )}
                </WorkContainer>
              </WorkBody>
              {isMobile ? (
                <Route path="/works/:owner_screen_name/:signed_ref/notes">
                  {({ match, ...rest }) => (
                    <Transition timeout={0} in={!!match}>
                      {state => (
                        <NoteBody
                          className={state}
                          work={work}
                          mobile={isMobile}
                          noteMode={!matchNotesRoute}
                          {...rest}
                        />
                      )}
                    </Transition>
                  )}
                </Route>
              ) : (
                <WorkDetailDesktop
                  work={work}
                  mobile={isMobile}
                  noteMode={!matchNotesRoute}
                  staticContext={staticContext}
                />
              )}
            </WorkAndDesktopNoteWrap>
          </WorkAndDesktopNoteWrapContainer>
          <WorkpageNavigationBar prevWork={prevWork} nextWork={nextWork} />
          {Boolean(data.related_works && data.related_works.length > 0) && (
            <RelatedWorks data={data} />
          )}

          {isOpenAboutModal && (
            <AboutModal
              isVisible={isOpenAboutModal}
              title={t('about')}
              description={data.description}
              onClose={this.handleOnPressCloseAboutModal}
            />
          )}
        </AllWorkInfoWrap>
        {data && data.secret && !noteMode && !isShowNote && (
          <CopyToClipboard
            text={`${getBaseUrl(staticContext)}${buildWorkPath(
              work.data,
              work.data.secret,
            )}`}
          >
            <PrivateWorkFloatingButton
              hideBadge
              onClick={this.handleOnClickCopyPrivateWorkUrl}
            >
              <PrivateWorkFloatingButtonContentContainer>
                <PrivateWorkFloatingButtonIconContainer>
                  <FontAwesomeIcon
                    icon={['fas', 'lock']}
                    color="#FBFBFB"
                    size={appWidth >= deviceSizes.laptop ? 60 : 28}
                  />
                </PrivateWorkFloatingButtonIconContainer>
                <div>
                  <PrivateWorkFloatingButtonTitle>
                    {t('copyPrivateWorkUrlFloatingButtonTitle')}
                  </PrivateWorkFloatingButtonTitle>
                  {appWidth >= deviceSizes.laptop && (
                    <PrivateWorkFloatingButtonDescription>
                      {t('copyPrivateWorkUrlFloatingButtonDescription')}
                    </PrivateWorkFloatingButtonDescription>
                  )}
                </div>
              </PrivateWorkFloatingButtonContentContainer>
            </PrivateWorkFloatingButton>
          </CopyToClipboard>
        )}
        {hasTiktokVideo && (
          <Helmet>
            <script async src="https://www.tiktok.com/embed.js" />
          </Helmet>
        )}
      </WorkNotePageWrap>
    );
  }
}

export default translate('workPage')(
  connect((state, props) => {
    const { works, user } = state;
    const { match } = props;
    const { signed_ref } = match.params;
    const matchedWork = findWorkStateBySignedRef(works, signed_ref);
    const work = {
      loading: true,
      ...matchedWork,
    };
    return {
      user,
      works,
      signed_ref,
      work_id: work.data?.id,
      work,
      error: work && work.error,
      ...props.initialData,
      match,
    };
  })(withResponsive(errorHandler(WorkPage))),
);
